"use client";

import { trackError } from "@repo/analytics";
import { fonts } from "@repo/design-system/lib/fonts";
import { AlertTriangle, RefreshCw } from "lucide-react";
import type NextError from "next/error";
import { useEffect } from "react";
import { Button } from "@/app/components/CustomButton";
import { StatusScreen } from "@/app/components/StatusScreen";

type GlobalErrorProperties = {
	readonly error: NextError & { digest?: string };
	readonly reset: () => void;
};

const GlobalError = ({ error, reset }: GlobalErrorProperties) => {
	useEffect(() => {
		const errorMessage =
			"message" in error ? (error.message as string) : "Unknown error";
		trackError(errorMessage, "global", error.digest || "");
	}, [error]);

	return (
		<html lang="en" className={fonts}>
			<body className="bg-primary text-primary">
				<StatusScreen
					tone="error"
					icon={<AlertTriangle />}
					title="Something went wrong"
					description="An unexpected error occurred. You can try again, and if the problem keeps happening, please reach out to our team."
					actions={
						<>
							<Button asChild variant="secondary">
								<a href="/">Go to dashboard</a>
							</Button>
							<Button onClick={() => reset()}>
								<RefreshCw />
								Try again
							</Button>
						</>
					}
					footer={
						error.digest ? (
							<p className="font-mono text-tertiary text-xs">
								Error ID: {error.digest}
							</p>
						) : null
					}
				/>
			</body>
		</html>
	);
};

export default GlobalError;
